import moment from "moment";
import React, { useEffect, useState } from "react";

import paidIcon from "../../assets/img/icons/paid-icon.svg";
import backgorund from "../../assets/img/invoice_background.svg";
import Logo from "../../assets/img/logo/logo.svg";
import {
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonText,
  DividerLine,
  InputWithLabelSection,
  PageTitle,
  TableText,
} from "../../styledComponents/common";
import {
  DashedDividerLine,
  PreviewDetailsContainer,
  PreviewDetailsElement,
  PreviewInfo,
  PreviewInfoTextContainer,
  PreviewInfoTotalContainer,
  TypeSpan,
} from "../../styledComponents/invoice";
import { currencySymbolMap } from "../../utils/currencies";
import { globalDateFormat } from "../../utils/helper";

const PreviewPDFView = ({
  invoiceDetails,
  invoiceConfig,
}) => {
  const {
    invoice_number,
    currency,
    client,
    client_name,
    issued_date,
    due_date,
    discounted_amount,
    paid_amount,
    total_amount,
    items,
    tax1,
    tax2,
    discount,
    status,
    total_tax1,
    total_tax2,
  } = invoiceDetails;

  const [totalCalc, setTotalCalc] = useState(0);

  useEffect(() => {
    if (items && items.length) {
      let total = 0;
      items.map(item => {
        total += parseFloat(item.quantity) * parseFloat(item.unit_price);
      });
      setTotalCalc(total.toFixed(2));
    }
  }, [items]);

  return (
    <div id="invoice">
      <PreviewDetailsContainer>
        {/* Top Section */}
        <CommonFlex
          padding="16px"
          justifyContent="space-between"
          alignItems="flex-start"
          bgColor="#EDF8FE"
        >
          {/* Organization Info */}
          <CommonGrid columns="1fr" gap="12px">
            {/* Logo & Name */}
            <CommonGrid gap="8px" justifyContent="start" alignItems="center">
              {invoiceConfig?.organization_logo_base64 && (
                <CommonImage
                  src={invoiceConfig?.organization_logo_base64}
                  size="32px"
                />
              )}
              <CommonText fontSize="20px" fontWeight="bold" name margin="0">
                {invoiceConfig?.name}
              </CommonText>
            </CommonGrid>

            {/* Address */}
            <CommonGrid columns="1fr" gap="8px" justifyContent="start">
              <CommonText fontSize="13px" color="#555555" margin="0">
                {invoiceConfig?.address}
              </CommonText>
              <CommonText fontSize="13px" color="#555555" margin="0">
                {invoiceConfig?.email}
              </CommonText>
              <CommonText fontSize="13px" color="#555555" margin="0">
                {invoiceConfig?.phone}
              </CommonText>
            </CommonGrid>
          </CommonGrid>
          <CommonText margin="0" fontSize="32px" fontWeight="bold" color="#2266EF">
            INVOICE
          </CommonText>
        </CommonFlex>

        <DashedDividerLine />

        {/* Invoice Info */}
        <CommonGrid gap="30px" columns="2fr 1fr 1fr 1fr">
          <InputWithLabelSection gap="4px">
            <CommonText name>Billed to</CommonText>
            <CommonText fontWeight="bold" name fontSize="16px">
              {client.name}
            </CommonText>
            <CommonText name>{client.email}</CommonText>
          </InputWithLabelSection>

          <InputWithLabelSection alignContent="flex-start">
            <CommonText name>Issue Date</CommonText>
            <CommonText name fontWeight="bold">
              {globalDateFormat(issued_date)}
            </CommonText>
          </InputWithLabelSection>

          <InputWithLabelSection alignContent="flex-start">
            <CommonText name>Due Date</CommonText>
            <CommonText
              name
              fontWeight="bold"
              style={{ marginLeft: !due_date ? "20px" : "0" }}
            >
              {due_date ? globalDateFormat(due_date) : "-"}
            </CommonText>
          </InputWithLabelSection>

          <InputWithLabelSection alignContent="flex-start">
            <CommonText name>Invoice no</CommonText>
            <CommonText name fontWeight="bold">
              {invoice_number}
            </CommonText>
          </InputWithLabelSection>
        </CommonGrid>

        <DashedDividerLine />

        <PreviewInfo>
          <PreviewInfoTextContainer>
            <CommonText name>#</CommonText>
            <CommonText name>Type</CommonText>
            <CommonText name>Item / Description</CommonText>
            <CommonText name>Quantity</CommonText>
            <CommonText name>Unit Price</CommonText>
            <CommonText name>Amount</CommonText>
          </PreviewInfoTextContainer>

          <DividerLine color="#E9F0FE" />

          <PreviewInfoTextContainer>
            {items.map((item, index) => (
              <React.Fragment key={index}>
                <TableText name>{index + 1}.</TableText>
                <TableText name>{item.type}</TableText>
                <TableText name>{item.description}</TableText>
                <TableText name>{item.quantity}</TableText>
                <TableText name>
                  {parseFloat(item.unit_price).toFixed(2)}
                </TableText>
                <TableText name>
                  {`${(
                    parseFloat(item.quantity) * parseFloat(item.unit_price)
                  ).toFixed(2)}`}
                </TableText>
              </React.Fragment>
            ))}
          </PreviewInfoTextContainer>

          <DividerLine color="#E9F0FE" />

          <PreviewInfoTotalContainer>
            {status == "paid" && (
              <CommonImage
                src={paidIcon}
                width="80px"
                style={{ position: "absolute", left: "100px", top: "15%" }}
              />
            )}
            <CommonText name>Subtotal</CommonText>
            <CommonText
              name
              fontWeight="bold"
            >{`${currencySymbolMap[currency]} ${totalCalc}`}</CommonText>

            {(parseFloat(discount.percent_amount) > 0 ||
              parseFloat(discount.fixed_amount) > 0) && (
              <>
                <CommonText name>
                  {discount.type === "percent"
                    ? `Discount (${parseFloat(discount.percent_amount).toFixed(
                        2,
                      )}%)`
                    : `Discount (${currencySymbolMap[currency]} ${parseFloat(
                        discount.fixed_amount,
                      ).toFixed(2)})`}
                </CommonText>
                <CommonText name fontWeight="bold">
                  {discount.type === "percent"
                    ? `- ${currencySymbolMap[currency]} ${(
                        parseFloat(total_amount) - parseFloat(discounted_amount)
                      ).toFixed(2)}`
                    : `- ${currencySymbolMap[currency]} ${parseFloat(
                        discount.fixed_amount,
                      ).toFixed(2)}`}
                </CommonText>
              </>
            )}
            {(parseFloat(tax1.percent_amount) > 0 ||
              parseFloat(tax1.fixed_amount) > 0) && (
              <>
                <CommonText name>
                  {tax1.name.toLowerCase() !== "tax 1" && `Tax 1 - `}
                  {tax1.type === "percent"
                    ? `${tax1.name} (${parseFloat(tax1.percent_amount).toFixed(
                        2,
                      )}%)`
                    : `${tax1.name} (${
                        currencySymbolMap[currency]
                      } ${parseFloat(tax1.fixed_amount).toFixed(2)})`}
                </CommonText>
                <CommonText name fontWeight="bold">
                  {currencySymbolMap[currency]} {total_tax1.toFixed(2)}
                </CommonText>
              </>
            )}
            {tax2 &&
              (parseFloat(tax2.percent_amount) > 0 ||
                parseFloat(tax2.fixed_amount) > 0) && (
                <>
                  <CommonText name>
                    {tax2 && tax2.name.toLowerCase() !== "tax 2" && `Tax 2 - `}
                    {tax2.type === "percent"
                      ? `${tax2.name} (${parseFloat(
                          tax2.percent_amount,
                        ).toFixed(2)}%)`
                      : `${tax2.name} (${
                          currencySymbolMap[currency]
                        } ${parseFloat(tax2.fixed_amount).toFixed(2)})`}
                  </CommonText>
                  <CommonText name fontWeight="bold">
                    {currencySymbolMap[currency]} {total_tax2.toFixed(2)}
                  </CommonText>
                </>
              )}
            <CommonText name>Grand Total</CommonText>
            <CommonText name fontWeight="bold">
              {`${currencySymbolMap[currency]} ${parseFloat(
                discounted_amount,
              ).toFixed(2)}`}
            </CommonText>
            <CommonText name>Paid</CommonText>
            <CommonText name fontWeight="bold">{`${currencySymbolMap[currency]} ${parseFloat(
              paid_amount,
            ).toFixed(2)}`}</CommonText>
          </PreviewInfoTotalContainer>

          <CommonGrid
            columns="162px 130px" 
            gap="10px"
            bgColor="#EDF8FE"
            padding="8px 0"
            justifyContent="end"
          >
            <CommonText name fontSize="13px">
              Amount Due
            </CommonText>
            <CommonText name fontWeight="bold">
              {currencySymbolMap[currency]}
              {(
                parseFloat(discounted_amount) - parseFloat(paid_amount)
              ).toFixed(2) > 0
                ? (
                    parseFloat(discounted_amount) - parseFloat(paid_amount)
                  ).toFixed(2)
                : 0}
            </CommonText>
          </CommonGrid>
        </PreviewInfo>

        {/* Signature Section */}
        <CommonGrid
          justifyContent="start"
          margin="100px 0 0 0"
          columns="150px 150px"
          padding="0 16px"
          gap="150px"
          alignContent="flex-end"
        >
          {/* Left: Signature */}
          <CommonGrid columns="1fr" gap="8px" alignContent="flex-end">
            {invoiceConfig?.digital_signature_base64 && (
              <CommonImage
                src={invoiceConfig.digital_signature_base64}
                // width="150px"
                // height="40px"
                style={{ objectFit: "contain", width: "150px", height: "40px" }}
              />
            )}
            <DividerLine color="#9DA6A5" />
            <CommonText name fontSize="13px">
              Signature
            </CommonText>
          </CommonGrid>

          {/* Right: Date */}
          <CommonGrid columns="1fr" gap="8px" alignContent="flex-end">
            <CommonText name fontSize="13px" fontWeight="bold">
              {globalDateFormat(issued_date)}
            </CommonText>
            <DividerLine color="#9DA6A5" />
            <CommonText name fontSize="13px">
              Date
            </CommonText>
          </CommonGrid>
        </CommonGrid>

        {/* {props.PDFView && ( */}
        <CommonFlex
          justifyContent="space-between"
          padding="12px 16px"
          bgColor="#EDF8FE"
          margin="50px 0 0 0"
        >
          <CommonText name fontSize="13px">
            Powered by
          </CommonText>
          <CommonImage src={Logo} width="100px" />
        </CommonFlex>
        {/* )} */}
      </PreviewDetailsContainer>
    </div>
  );
};

export default PreviewPDFView;
